import { uniq } from 'lodash-unified';
import { envConfig } from '@bg-shared/constants/envConfig';
import { CHAT_ADMIN_ALIAS, CHAT_MESSAGE_MAX_LENGTH } from '@bg-shared/constants/constants';
import { sessionStore } from '@bg-shared/business/Session/Session.store';
import { GameId } from '@bg-shared/enums/GameId';
import { chatApi } from './Chat.api';
import { chatStore } from './Chat.store';
import { AmityUserBanCodes, AmityUserUnauthorizedCodes } from './enums';
import {
    IChatStoreModel,
    INormalizedUserAuth,
    IAmityServerError,
    IBackendServerError,
} from './interfaces';

class ChatService {
    private config: IChatConfig = envConfig.chatConfig;

    private sessionHandler: Amity.SessionHandler;

    private gameId: GameId;

    private amitySDK: typeof import('@amityco/ts-sdk');

    private unsubscribeMessageUpdates: (() => void)[] = [];

    private USER_BAN_CODES = Object.values(AmityUserBanCodes).filter(Number);

    private USER_UNAUTHORIZED_CODES = Object.values(AmityUserUnauthorizedCodes).filter(Number);

    private isInitialized = false;

    private async ensureInitialized(): Promise<void> {
        if (!this.isInitialized) {
            this.isInitialized = true;
            await this.initiateClient();
        }
    }

    private async loadSDK(): Promise<typeof import('@amityco/ts-sdk')> {
        if (!this.amitySDK) {
            try {
                this.amitySDK = await import('@amityco/ts-sdk');
            } catch (e) {
                console.warn('Cant load Chat', e);
            }
        }

        return this.amitySDK;
    }

    private isAmityServerError(error: unknown): error is IAmityServerError {
        return error && typeof error === 'object' && 'code' in error && 'message' in error;
    }

    private isBackendServerError(error: unknown): error is IBackendServerError {
        return typeof error === 'object' && 'error' in error;
    }

    private handleError(error?: unknown): void {
        if (!error) {
            chatStore.resetError();
            return;
        }

        let errorMessage = '';

        if (this.isAmityServerError(error)) {
            errorMessage = error.message;
            if (this.USER_BAN_CODES.includes(error?.code)) {
                chatStore.setIsBanned();
            } else if (this.USER_UNAUTHORIZED_CODES.includes(error?.code)) {
                chatStore.resetChannelConnection();
            }
        } else if (this.isBackendServerError(error)) {
            // Todo - fix translation key
            errorMessage = (error.error as string).replace(/\[\w+\]/g, '').trim();
        } else if (error instanceof Error) {
            errorMessage = error.message;
        } else if (typeof error === 'string') {
            errorMessage = error;
        }

        chatStore.setError(errorMessage);

        console.error('Chat error | ', errorMessage, error);
    }

    private errorHandler = this.handleError.bind(this);

    private async initiateClient(): Promise<void> {
        if (!this.config?.apiKey) {
            return;
        }

        const { Client, enableCache } = await this.loadSDK();
        Client.createClient(this.config.apiKey, this.config.region);

        this.sessionHandler = {
            sessionWillRenewAccessToken: async (renewal) => {
                try {
                    const { authToken } = await this.getUserAuth();
                    renewal.renewWithAuthToken(authToken);
                } catch (error) {
                    this.handleError(error);
                    renewal.unableToRetrieveAuthToken();
                }
            },
        };

        enableCache();
    }

    private async getUserAuth(): Promise<INormalizedUserAuth> {
        return chatApi
            .getAuth()
            .then((response) => ({
                userId: response.userId,
                authToken: response.token,
                displayName: response.nickname,
            }))
            .then((response) => {
                chatStore.setUserAuthData(response.displayName, response.userId);
                return response;
            });
    }

    private async fetchMessages(): Promise<void> {
        const { MessageRepository } = await this.loadSDK();

        MessageRepository.getMessages(
            { subChannelId: chatStore.channelId, limit: 50 },
            ({ data: messages, error }) => {
                if (messages) {
                    chatStore.setMessages(messages as IChatStoreModel['messages']);
                    this.getUserNames(messages);
                }

                this.handleError(error);
            },
        );
    }

    private isAdminUser(user: Amity.User): boolean {
        return !!user.roles.length;
    }

    private async getUserNames(messageArray: Amity.Message[]): Promise<void> {
        const userIds = uniq(
            messageArray.map((item) => item.creatorId).filter((id) => !chatStore.userNamesList[id]),
        );

        if (!userIds.length) {
            chatStore.setLoading();
            return;
        }

        const { UserRepository } = await this.loadSDK();

        Promise.all(
            [...userIds].map((userId) => {
                return new Promise<void>((resolve) => {
                    UserRepository.getUser(userId, ({ data, error }) => {
                        if (error) {
                            this.handleError(error);
                        }
                        if (data) {
                            chatStore.addUsername(
                                userId,
                                this.isAdminUser(data) ? CHAT_ADMIN_ALIAS : data.displayName,
                            );
                        }
                        resolve();
                    });
                });
            }),
        )
            .catch(this.errorHandler)
            .finally(() => {
                chatStore.setLoading();
            });
    }

    private async subscribeToChannel(channelId: string): Promise<void> {
        if (chatStore.channelId === channelId) {
            return;
        }

        const { ChannelRepository } = await this.loadSDK();
        await ChannelRepository.joinChannel(channelId).catch(this.errorHandler);
        chatStore.setChannelId(channelId);
    }

    private async checkUserRegistration(): Promise<boolean> {
        if (chatStore.isRegistered) {
            return true;
        }

        return this.getUserAuth()
            .then(() => true)
            .catch(() => false);
    }

    private async subscribeMessagesUpdates(channelId: string): Promise<void> {
        if (this.unsubscribeMessageUpdates.length) {
            return;
        }

        const handler = (channel: Amity.InternalChannel, user: Amity.Membership<'channel'>) => {
            if (channel._id === channelId && user.userId === chatStore.userId) {
                chatStore.resetChannelConnection();

                if (user.isBanned || user.isMuted) {
                    chatStore.setIsBanned();
                }
            }
        };

        const { ChannelRepository } = await this.loadSDK();

        this.unsubscribeMessageUpdates.push(
            ChannelRepository.onChannelMemberRemoved(handler),
            ChannelRepository.onChannelMemberBanned(handler),
        );
    }

    private unsubscribeMessagesUpdates(): void {
        this.unsubscribeMessageUpdates.forEach((unsubscribe) => {
            unsubscribe?.();
        });
    }

    public async login(): Promise<void> {
        chatStore.setLoading(true);

        const { authToken, userId, displayName } = await this.getUserAuth();
        const { Client } = await this.loadSDK();

        Client.login(
            {
                userId,
                displayName,
                authToken,
            },
            this.sessionHandler,
        )
            .then(() => {
                chatStore.setUserLoggedIn();
                chatStore.setLoading();
                sessionStore.update({ nickname: displayName });
            })
            .catch(this.errorHandler);
    }

    public async logout(): Promise<void> {
        if (!chatStore.isLoggedIn) {
            return;
        }

        const { Client } = await this.loadSDK();

        this.unsubscribeMessagesUpdates();
        this.leaveChannel();
        Client.secureLogout();
        chatStore.resetState();
    }

    public async leaveChannel(): Promise<void> {
        if (!chatStore.isJoinedChannel) {
            return;
        }

        const { ChannelRepository } = await this.loadSDK();

        ChannelRepository.leaveChannel(chatStore.channelId)
            .then(() => {
                chatStore.resetChannelConnection();
            })
            .catch(this.errorHandler);
    }

    public isValidMessage(message: string): boolean {
        if (!message || !message.trim() || message.length > CHAT_MESSAGE_MAX_LENGTH) {
            return false;
        }

        return true;
    }

    public async sendMessage(message: string): Promise<void> {
        if (!chatStore.isJoinedChannel || !this.isValidMessage(message)) {
            return;
        }

        const { MessageRepository, MessageContentType } = await this.loadSDK();

        MessageRepository.createMessage({
            subChannelId: chatStore.channelId,
            data: { text: message },
            dataType: MessageContentType.TEXT,
        }).catch(this.errorHandler);
    }

    public async register(screenName: string): Promise<void> {
        await this.ensureInitialized();

        const isRegistered = await chatApi
            .createNickName(screenName)
            .then((response) => {
                chatStore.setUserAuthData(screenName, response.userId);
                return true;
            })
            .catch((error) => {
                this.handleError(error);
                chatStore.setLoading();
                return false;
            });

        if (isRegistered) {
            await this.login();
        }
    }

    public async initializeSession(): Promise<void> {
        if (!this.config || chatStore.isLoggedIn) {
            return;
        }

        await this.ensureInitialized();

        chatStore.setLoading(true);

        const isRegistered = await this.checkUserRegistration();

        if (isRegistered) {
            this.login();
        } else {
            chatStore.setLoading();
        }
    }

    public async initializeConversion(): Promise<void> {
        if (!this.config || chatStore.isJoinedChannel) {
            return;
        }

        await this.ensureInitialized();

        chatStore.setLoading(true);

        const { channelId } = this.config.games?.[this.gameId] || {};
        await this.subscribeToChannel(channelId);
        await this.fetchMessages();
        this.subscribeMessagesUpdates(channelId);
    }

    public setGameId(gameId: GameId): void {
        this.gameId = gameId;
    }
}

export const chatService = new ChatService();
